import { BaseVisualizer } from './BaseVisualizer';

/**
 * Particle structure with physics properties
 */
interface Particle {
  x: number;
  y: number;
  vx: number;      // velocity x
  vy: number;      // velocity y
  size: number;    // 1-5 pixels
  color: { h: number; s: number; l: number };
  life: number;    // 0-1, decreases over time
  maxLife: number; // initial life
  mass: number;    // for gravity (based on pitch)
}

/**
 * Particles Visualizer - Explosive particle system reacting to music
 * 
 * Features:
 * - Thousands of particles flying from center outward
 * - Massive beat-reactive explosions
 * - Physics-based movement (gravity, velocity, friction)
 * - Frequency-based particle colors (bass=blue, mid=green, treble=yellow/white)
 * - Gravity affects particles based on frequency (bass falls, treble rises)
 * - Trail effects for motion blur
 * - Screen flash on beats
 * - Intro animation with gradual particle buildup
 * - Idle state with minimal particles
 */
export class ParticlesVisualizer extends BaseVisualizer {
  private particles: Particle[] = [];
  private maxParticles: number = 1500; // Reduced from 5000
  private flashIntensity: number = 0;
  private visualizerState: 'idle' | 'intro' | 'running' = 'idle';
  private introStartTime: number = 0;
  private introDuration: number = 2.5; // Match PsychedelicVisualizer
  private frameCount: number = 0;
  private centerX: number = 0;
  private centerY: number = 0;
  private hasStartedOnce: boolean = false;
  
  constructor(canvas: HTMLCanvasElement) {
    super(canvas, 180);
    this.updateCenter();
  }

  /**
   * Update center position
   */
  private updateCenter(): void {
    this.centerX = this.canvas.width / 2;
    this.centerY = this.canvas.height / 2;
  }

  /**
   * Update center on resize
   */
  protected resize(): void {
    super.resize();
    this.updateCenter();
  }

  /**
   * Start intro animation
   */
  public startIntro(): void {
    if (this.hasStartedOnce && this.visualizerState === 'running') {
      // Just spawn a burst, don't restart intro
      console.log('💥 Particles burst (already running)');
      this.spawnExplosion(100 + this.currentLoudness * 100, 0.9);
      this.flashIntensity = 0.6;
      return;
    }
    
    console.log('💥 Starting Particles intro animation');
    this.visualizerState = 'intro';
    this.introStartTime = this.currentTime;
    this.hasStartedOnce = true;
  }

  /**
   * Get intro progress (0-1)
   */
  private getIntroProgress(): number {
    if (this.visualizerState !== 'intro') return 1;
    const elapsed = this.currentTime - this.introStartTime;
    return Math.min(1, elapsed / this.introDuration);
  }

  /**
   * Check if in intro
   */
  private isInIntro(): boolean {
    return this.visualizerState === 'intro' && this.getIntroProgress() < 1;
  }

  /**
   * Main render loop
   */
  protected render(): void {
    this.frameCount++;
    
    // Debug logging every 2 seconds
    if (this.frameCount % 120 === 0 && this.visualizerState === 'running') {
      console.log(`[PARTICLES] Beat: ${this.currentBeat.toFixed(2)}, Bar: ${this.currentBar.toFixed(2)}, Energy: ${this.currentEnergy.toFixed(2)}, Particles: ${this.particles.length}`);
    }

    // Handle idle state
    if (this.visualizerState === 'idle') {
      this.renderIdleState();
      return;
    }

    // Check if intro is complete
    if (this.visualizerState === 'intro' && this.getIntroProgress() >= 1) {
      this.visualizerState = 'running';
      console.log('💥 Particles intro complete - entering running state');
    }

    // Dark background with fade trail for motion blur
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Get intro progress
    const introProgress = this.getIntroProgress();
    const isInIntro = this.isInIntro();

    // Update flash intensity
    this.updateFlash();

    // Spawn particles based on state
    if (isInIntro) {
      this.spawnIntroParticles(introProgress);
    } else {
      this.spawnContinuousParticles();
      this.spawnBeatExplosion();
      this.spawnBarWave();
    }

    // Update particle physics
    this.updateParticles();

    // Draw particles
    this.drawParticles();

    // Draw screen flash
    if (this.flashIntensity > 0) {
      this.drawFlash();
    }

    // Draw intro hint text
    if (isInIntro && introProgress < 0.3) {
      this.drawIntroText();
    }
  }

  /**
   * Render idle state with minimal particles
   */
  private renderIdleState(): void {
    // Dark background
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Spawn occasional particles
    if (Math.random() < 0.1) {
      this.spawnParticle(0.2, Math.random() * 360);
    }

    // Update and draw particles
    this.updateParticles();
    this.drawParticles();

    // Draw hint text
    this.ctx.save();
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    this.ctx.font = '48px Arial, sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('💥 PARTICLES', this.centerX, this.centerY);
    this.ctx.restore();
  }

  /**
   * Spawn particles during intro
   * Gradual buildup to BIG explosion at 98%
   */
  private spawnIntroParticles(progress: number): void {
    // 0-25%: Few particles start appearing
    if (progress < 0.25) {
      if (Math.random() < 0.2) {
        this.spawnParticle(0.2 + progress * 0.3, Math.random() * 360);
      }
    }
    // 25-50%: Particle spawn rate increases gradually
    else if (progress < 0.5) {
      const spawnChance = (progress - 0.25) * 2; // 0 to 0.5
      if (Math.random() < spawnChance) {
        this.spawnParticle(0.3 + progress * 0.4, Math.random() * 360);
      }
    }
    // 50-75%: First small burst
    else if (progress < 0.75) {
      if (Math.random() < 0.15) {
        this.spawnExplosion(20 + progress * 30, 0.5 + progress * 0.3);
      }
    }
    // 75-98%: Speed increases, more bursts
    else if (progress < 0.98) {
      if (Math.random() < 0.3) {
        this.spawnExplosion(40 + progress * 60, 0.7 + progress * 0.2);
      }
    }
    // 98%: BIG EXPLOSION!
    else if (progress >= 0.98 && progress < 0.99) {
      // Massive finale burst (only trigger once)
      this.spawnExplosion(200, 1.0);
      this.flashIntensity = 0.8;
    }
  }

  /**
   * Spawn continuous particles based on energy
   * MINIMAL continuous spawning - explosions are dramatic!
   */
  private spawnContinuousParticles(): void {
    // DRASTICALLY REDUCED continuous spawning to make beats OBVIOUS
    // Only spawn occasionally (10% chance per frame = ~6 particles/second at 60fps)
    if (Math.random() < 0.1) {
      const angle = Math.random() * 360;
      const speed = 5 + this.currentEnergy * 10; // 5-15 pixels/frame (MUCH faster!)
      this.spawnParticle(speed, angle);
    }
  }

  /**
   * Spawn DRAMATIC beat explosions
   * Beat explosions should be the MAIN visual event!
   */
  private spawnBeatExplosion(): void {
    // Match PsychedelicVisualizer threshold: 0.6 for more frequent reactions
    if (this.currentBeat > 0.6) {
      // REMOVED cooldown to match PsychedelicVisualizer - let EVERY beat trigger!
      // This ensures particles react IMMEDIATELY and OBVIOUSLY to music
      
      // MASSIVE explosion: 100-300 particles
      const burstCount = 100 + this.currentLoudness * 200; // 100-300 particles
      const intensity = 0.8 + this.currentBeat * 0.2;
      
      console.log(`💥 BEAT DETECTED! Beat: ${this.currentBeat.toFixed(2)}, spawning ${Math.floor(burstCount)} particles`);
      
      this.spawnExplosion(burstCount, intensity);
      
      // VERY VISIBLE screen flash
      this.flashIntensity = 0.8;
    }
  }

  /**
   * Spawn gravitational wave on bar
   */
  private spawnBarWave(): void {
    if (this.currentBar > 0.75) {
      // Push all existing particles outward
      const pushForce = this.currentBar * 2;
      
      for (const particle of this.particles) {
        const dx = particle.x - this.centerX;
        const dy = particle.y - this.centerY;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        
        particle.vx += (dx / dist) * pushForce;
        particle.vy += (dy / dist) * pushForce;
      }
      
      // Spawn additional particles
      if (Math.random() < 0.5) {
        this.spawnExplosion(50 + this.currentBar * 50, 0.7);
      }
    }
  }

  /**
   * Spawn an explosion of particles
   */
  private spawnExplosion(count: number, intensity: number): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * 360;
      const speed = intensity * (8 + Math.random() * 17); // 8-25 pixels/frame (EXTREMELY fast!)
      this.spawnParticle(speed, angle);
    }
  }

  /**
   * Spawn a single particle
   */
  private spawnParticle(speed: number, angleDeg: number): void {
    // Limit particle count
    if (this.particles.length >= this.maxParticles) {
      // Remove oldest particle
      this.particles.shift();
    }

    // Get dominant pitch for color
    const pitchIndex = this.getDominantPitch();
    
    // Map pitch to color (HSL hue)
    let hue: number;
    let mass: number;
    
    if (pitchIndex < 4) {
      // Low pitches: Blue/Purple (heavy, fall down)
      hue = 240 + pitchIndex * 10; // 240-270 (blue to purple)
      mass = 0.15 + Math.random() * 0.1; // Heavy
    } else if (pitchIndex < 9) {
      // Mid pitches: Green/Cyan (medium)
      hue = 150 + (pitchIndex - 4) * 15; // 150-225 (green to cyan)
      mass = 0.05 + Math.random() * 0.05; // Medium
    } else {
      // High pitches: Yellow/White (light, rise up)
      hue = 50 + (pitchIndex - 9) * 10; // 50-80 (yellow to white)
      mass = -0.05 - Math.random() * 0.1; // Light (negative = rise up)
    }

    // Add some color variation
    hue += Math.random() * 20 - 10;

    // Calculate velocity
    const angleRad = (angleDeg * Math.PI) / 180;
    const vx = Math.cos(angleRad) * speed;
    const vy = Math.sin(angleRad) * speed;

    // Create particle with LARGER size and LONGER lifetime
    const particle: Particle = {
      x: this.centerX,
      y: this.centerY,
      vx,
      vy,
      size: 2 + Math.random() * 4 + this.currentLoudness * 2, // 2-8 pixels (larger!)
      color: {
        h: hue,
        s: 90 + Math.random() * 10, // 90-100% saturation (more vibrant!)
        l: 55 + this.currentLoudness * 25, // Brighter with loudness
      },
      life: 1,
      maxLife: 2.0 + Math.random() * 2.0, // 2-4 seconds (much longer to reach screen edges!)
      mass,
    };

    this.particles.push(particle);
  }

  /**
   * Get dominant pitch from current pitches
   */
  private getDominantPitch(): number {
    if (!this.currentPitches || this.currentPitches.length === 0) {
      return Math.floor(Math.random() * 12);
    }

    // Find index of highest pitch value
    let maxIndex = 0;
    let maxValue = this.currentPitches[0];

    for (let i = 1; i < this.currentPitches.length; i++) {
      if (this.currentPitches[i] > maxValue) {
        maxValue = this.currentPitches[i];
        maxIndex = i;
      }
    }

    return maxIndex;
  }

  /**
   * Update particle physics
   */
  private updateParticles(): void {
    const deltaTime = 1 / 60; // Assume 60 FPS
    const gravityStrength = 0.05;
    const friction = 0.98;

    // Update each particle
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];

      // Apply gravity (based on mass)
      p.vy += p.mass * gravityStrength;

      // Apply friction
      p.vx *= friction;
      p.vy *= friction;

      // Update position
      p.x += p.vx;
      p.y += p.vy;

      // Decrease life
      p.life -= deltaTime / p.maxLife;

      // Remove if dead or out of bounds
      if (p.life <= 0 || this.isOutOfBounds(p)) {
        this.particles.splice(i, 1);
      }
    }
  }

  /**
   * Check if particle is out of bounds
   */
  private isOutOfBounds(p: Particle): boolean {
    const margin = 100;
    return (
      p.x < -margin ||
      p.x > this.canvas.width + margin ||
      p.y < -margin ||
      p.y > this.canvas.height + margin
    );
  }

  /**
   * Draw all particles
   */
  private drawParticles(): void {
    this.ctx.save();

    for (const particle of this.particles) {
      const alpha = Math.max(0, particle.life);

      // Outer glow
      this.ctx.shadowBlur = 15;
      this.ctx.shadowColor = this.hsl(
        particle.color.h,
        100,
        50,
        alpha * 0.5
      );
      this.ctx.fillStyle = this.hsl(
        particle.color.h,
        particle.color.s,
        60,
        alpha * 0.3
      );
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, particle.size * 2, 0, Math.PI * 2);
      this.ctx.fill();

      // Inner core
      this.ctx.fillStyle = this.hsl(
        particle.color.h,
        particle.color.s,
        particle.color.l,
        alpha
      );
      this.ctx.beginPath();
      this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      this.ctx.fill();
    }

    this.ctx.restore();
  }

  /**
   * Update flash intensity (decay over time)
   */
  private updateFlash(): void {
    if (this.flashIntensity > 0) {
      this.flashIntensity *= 0.85; // Fade quickly
      if (this.flashIntensity < 0.01) {
        this.flashIntensity = 0;
      }
    }
  }

  /**
   * Draw screen flash
   */
  private drawFlash(): void {
    this.ctx.save();
    
    // Get dominant color from current pitches
    const pitchIndex = this.getDominantPitch();
    let hue = 0;
    
    if (pitchIndex < 4) {
      hue = 240; // Blue
    } else if (pitchIndex < 9) {
      hue = 180; // Cyan
    } else {
      hue = 60; // Yellow
    }

    // Full-screen flash
    this.ctx.fillStyle = this.hsl(hue, 100, 70, this.flashIntensity * 0.4);
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.restore();
  }

  /**
   * Draw intro hint text
   */
  private drawIntroText(): void {
    this.ctx.save();
    
    const alpha = 0.6 * (1 - this.getIntroProgress() / 0.3);
    this.ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
    this.ctx.font = '48px Arial, sans-serif';
    this.ctx.textAlign = 'center';
    this.ctx.textBaseline = 'middle';
    this.ctx.fillText('💥 PARTICLES', this.centerX, this.centerY);

    this.ctx.restore();
  }
}
